<template>
  <v-col
    v-bind="{ ...containerProps, ...containerOptions }"
    class="helmi-range-slider"
  >
    <helmi-subheader
      v-if="$attrs.label"
      :colors="labelColors || colors"
      :icon="$attrs.icon"
      :label="label"
      class="pa-0 mb-0"
    />

    <v-row align="center" no-gutters>
      <v-col
        class="text-body-2 font-weight-bold accent--text text--lighten-2"
        cols="6"
      >
        {{ formatValue(range[modelValue[0]]) }}
      </v-col>
      <v-col
        class="text-right text-body-2 font-weight-bold accent--text text--lighten-2"
        cols="6"
      >
        {{ formatValue(range[modelValue[1]]) }}
        {{ modelValue[1] === range.length - 1 ? '+' : '' }}
      </v-col>
      <v-col cols="12">
        <v-range-slider
          :id="$attrs.id"
          v-model="modelValue"
          :class="`${$attrs.id}__range-slider`"
          v-bind="$attrs"
          :color="$attrs.color || colors.base"
          :max="range.length - 1"
          label=""
          min="0"
          track-color="grey lighten-1"
          step="1"
          hide-details
        />
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { EventBus } from '@/utils';

const getDefaultState = () => ({
  defaultValue: [],
  modelValue: [],
  previousRange: [],
});

export default {
  name: 'HelmiRangeSlider',

  components: {
    'helmi-subheader': () => import('@/components/formElements/Subheader'),
  },

  inheritAttrs: false,

  props: {
    chapter: {
      type: String,
      default: '',
    },

    colors: {
      type: Object,
      default: () => ({
        base: 'primary',
        text: 'primary--text',
      }),
    },

    containerProps: {
      type: Object,
      default: () => ({}),
    },

    containerOptions: {
      type: Object,
      default: () => ({}),
    },

    labelColors: {
      type: Object,
      default: () => null,
    },

    propertyValue: {
      type: Array,
      default: () => [],
    },

    stepOptions: {
      type: Object,
      default: () => ({
        limit: null,
        delta: null,
      }),
    },
  },

  data: getDefaultState,

  computed: {
    label() {
      return this.$attrs.chapter ? `${this.chapter} ${this.$attrs.label}` : this.$attrs.label;
    },

    range() {
      return this.calculateRange(
        parseInt(this.$attrs.min, 10),
        parseInt(this.$attrs.max, 10) + parseInt(this.$attrs.step, 10),
      );
    },
  },

  watch: {
    modelValue(newVal) {
      this.$emit('input', {
        [`${this.$attrs.id}_min`]: this.range[newVal[0]],
        [`${this.$attrs.id}_max`]: this.range[newVal[1]],
      });
    },

    range(r) {
      // prevent the infinite loop
      if (
        r.every(v => this.previousRange.includes(v))
        && r.length === this.previousRange.length
      ) return;

      this.previousRange = r;
      this.setInitialValue();
    },
  },

  mounted() {
    EventBus.$on('FORM_RESET', this.resetValue);

    this.setInitialValue();
  },

  methods: {
    formatValue(value) {
      if (this.$attrs.suffix === '€') return this.$options.filters.currency(value, { digits: 0 });
      if (this.$attrs.suffix) return `${value} ${this.$attrs.suffix}`;
      return value;
    },

    calculateRange(min, max) {
      let delta = this.stepOptions && this.stepOptions.step
        ? this.stepOptions.step[0]
        : parseInt(this.$attrs.step, 10);
      let current = min;
      const values = [];
      const limit = this.stepOptions && this.stepOptions.limit
        ? this.stepOptions.limit
        : max;

      while (current < max) {
        values.push(current);

        if (current >= limit) {
          delta = this.stepOptions && this.stepOptions.step
            ? this.stepOptions.step[1]
            : 50000;
        }

        current += delta;
      }

      return values;
    },

    resetValue() {
      Object.assign(this.$data, {
        modelValue: [0, this.range.length],
        defaultValue: [0, this.range.length],
      });
    },

    setInitialValue() {
      const { id, value } = this.$attrs;
      const min = this.range.indexOf(value?.[`${id}_min`]);
      const max = this.range.indexOf(value?.[`${id}_max`]);

      this.modelValue = [
        min >= 0 ? min : 0,
        max >= 0 ? max : this.range.length,
      ];
      this.defaultValue = [0, this.range.length];
    },
  },
};
</script>

<style lang="scss">
.helmi-range-slider {
  position: relative;

  .v-input {
    align-items: center;
  }
}
</style>
